import { FC, Fragment, useCallback, useEffect, useState, useMemo } from 'react';
import { ScheduleFeedItem } from '../scheduleFeed/scheduleFeedItem/scheduleFeedItem';
import { timeslotsStore } from 'entities/timeslots';
import { addLeadingZero } from 'shared/lib/string';
import { formatWithLocale } from 'shared/lib/date';
import { observer } from 'mobx-react-lite';
import { useSwipeable } from 'react-swipeable';
import { bookingStore } from 'entities/booking';
import { CalendarDay, ScheduleCalendarProps } from './types';
import chevronLeft from 'assets/chevron-left.svg';
import chevronRight from 'assets/chevron-right.svg';
import chevronLeftDisable from 'assets/chevron-left-disable.svg';
import chevronRightDisable from 'assets/chevron-right-disable.svg';
import styles from './styles.module.scss';
import { ButtonContained } from 'shared/ui/buttons/buttonContained';
import { addMonths, subMonths } from 'date-fns';

export const ScheduleCalendar: FC<ScheduleCalendarProps> = observer(
	({
		isOpen = false,
		day,
		dateCalendarStart,
		close
	}) => {
		const MAX_MONTHS_COUNT = addMonths(dateCalendarStart, 3);
		const [selectedDay, setSelectedDay] = useState('');
		const [firstScheduleDayInMonth, setFirstScheduleDayInMonth] = useState(
			new Date(),
		);
		const [currentDate, setCuttentDate] = useState<Date>(dateCalendarStart);

		const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
		const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

		const getMonthName = (date: Date) =>
			date
				.toLocaleString('default', { month: 'long', year: 'numeric' })
				.toLocaleLowerCase()
				.replace(' г.', '')
				.toLocaleUpperCase();

		const handleChangeMonth = (direction: 'prev' | 'next') => () => {
			if (direction === 'prev') {
				setCuttentDate((prev: Date) => {
					if (prev.getTime() > dateCalendarStart.getTime()) {
						return new Date(subMonths(prev, 1));
					}

					return new Date(prev);
				});
			} else {
				setCuttentDate(prev => {
					if (prev.getTime() < MAX_MONTHS_COUNT.getTime()) {
						return new Date(addMonths(prev, 1));
					}

					return new Date(prev);
				});
			}
		};

		const getDaysInMonth = useCallback(
			(date: Date) => {
				const year = date.getFullYear();
				const month = date.getMonth();

				const firstDay = new Date(year, month, 1);
				const firstDayOfWeek = firstDay.getDay();
				const lastDay = new Date(year, month + 1, 0);
				const daysInMonth = lastDay.getDate();

				const daysArray: CalendarDay[] = [];

				for (
					let i = 0;
					i < (firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1);
					i++
				) {
					daysArray.push({ isEmpty: true });
				}

				const monthStr = formatWithLocale(date, 'MM');
				for (let day = 1; day <= daysInMonth; day++) {
					const dateKey = `${year}-${monthStr}-${addLeadingZero(day)}`;
					const monthDaySlot =
						timeslotsStore.monthSlotsMap.get(dateKey);
					const currentTime = new Date(dateKey);

					firstScheduleDayInMonth.setHours(0, 0, 0, 0);

					const calendarDay: CalendarDay = {
						day,
						date: dateKey,
						branchesIds: [],
						inSchedule: Boolean(monthDaySlot),
						isAccesible: monthDaySlot === false,
						isPassed:
							currentTime.getTime() <
							firstScheduleDayInMonth.getTime(),
					};

					daysArray.push(calendarDay);
				}

				return daysArray;
			},
      // eslint-disable-next-line react-hooks/exhaustive-deps
			[timeslotsStore.monthSlotsMap, firstScheduleDayInMonth],
		);

		const handleSwipeLeft = () => {
			handleChangeMonth('next')();
		};

		const handleSwipeRight = () => {
			handleChangeMonth('prev')();
		};

		const handlers = useSwipeable({
			onSwipedLeft: handleSwipeLeft,
			onSwipedRight: handleSwipeRight,
		});

		const handleSelectDay = async (date: string) => {
			const monthDaySlot = timeslotsStore.monthSlotsMap.get(date);
			if (monthDaySlot === true) {
				setSelectedDay(date);
				await bookingStore.setDate(date);
				close();
			}
		};

		const isActiveDay = (calendarDay: CalendarDay) => {
			if (calendarDay.date === selectedDay) {
				return true;
			} else {
				return calendarDay.inSchedule ? false : undefined;
			}
		};

		useEffect(() => {
			if (currentDate) {
				const days = getDaysInMonth(currentDate);
				setCalendarDays(days);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [currentDate, timeslotsStore.monthSlotsMap.size, getDaysInMonth]);

		useEffect(() => {
			if (timeslotsStore.monthSlotsMap.size) {
				const monthSlotsDays = Array.from(
					timeslotsStore.monthSlotsMap.keys(),
				);
				const firstMonthSlotsDay = monthSlotsDays.shift() as string;
				setFirstScheduleDayInMonth(new Date(firstMonthSlotsDay));
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [timeslotsStore.monthSlotsMap.size]);

		useEffect(() => {
			if (day) {
				const selectedDate = new Date(day);
				setCuttentDate(selectedDate);
				setSelectedDay(day);
			}
		}, [day, isOpen]);

		const isPrevDisabled = useMemo(() => {
			return currentDate.getMonth() === dateCalendarStart.getMonth();
      // eslint-disable-next-line
		}, [currentDate]);

		const isNextDisabled = useMemo(() => {
			if (currentDate && MAX_MONTHS_COUNT) {
				return currentDate.getTime() >= MAX_MONTHS_COUNT.getTime();
			}
		}, [currentDate, MAX_MONTHS_COUNT]);

		return (
			<div className="calendar" {...handlers}>
				{isOpen && (
					<Fragment>
						<div className={styles.calendar__header}>
							<ButtonContained
								onClick={handleChangeMonth('prev')}
								disabled={isPrevDisabled}
								variant={'transparent'}
							>
								<img
									className={styles.calendar__img}
									src={
										isPrevDisabled
											? chevronLeftDisable
											: chevronLeft
									}
								/>
							</ButtonContained>

							<div className={styles.calendar__title}>
								{getMonthName(currentDate)}
							</div>

							<ButtonContained
								onClick={handleChangeMonth('next')}
								disabled={isNextDisabled}
								variant={'transparent'}
							>
								<img
									className={styles.calendar__img}
									src={
										isNextDisabled
											? chevronRightDisable
											: chevronRight
									}
								/>
							</ButtonContained>
						</div>

						<div className={styles.calendar__grid}>
							{weekdays.map((day, index) => (
								<div
									key={index}
									className={styles.calendar__weekday}
								>
									{day}
								</div>
							))}

							{calendarDays.map((calendarDay, index) => (
								<ScheduleFeedItem
									key={index}
									date={calendarDay.date}
									day={calendarDay.day}
									isDisable={calendarDay.isAccesible}
									isActive={isActiveDay(calendarDay)}
									isCalendarDay={true}
									dayDisable={calendarDay.isPassed}
									selectDay={handleSelectDay}
								/>
							))}
						</div>
					</Fragment>
				)}
			</div>
		);
	},
);
